import Vue from "vue"
import { ActionTree, MutationTree, GetterTree } from "vuex"
import { RootState } from "~/store"
import Band from "~/types/band"

import picture_build_helper, {
  PictureTarget,
  PictureSize,
} from "~/helpers/images/picture_build"

export const state = () => {
  return [] as Band[]
}
export type BandSetState = ReturnType<typeof state>

export const mutations: MutationTree<BandSetState> = {
  RESET(state) {
    state.splice(0, state.length)
  },
  SET(state, bandSet: Band[]) {
    for (let i = bandSet.length - 1; i >= 0; i--) {
      if (!bandSet[i].media_links)
        bandSet[i].media_links = {
          instagram: "",
          facebook: "",
          youtube: "",
          deezer: "",
          spotify: "",
          bandcamp: "",
          soundcloud: "",
          twitter: "",
          pk: 0,
          id: 0,
        }
      state.push(bandSet[i])
    }
  },
  UPDATE_INDEX_WITH_PATCH: function (
    state,
    { band_patch, index }: { band_patch: Band; index: number }
  ) {
    let key: keyof Band

    for (key in band_patch) {
      const data = band_patch[key]

      if (typeof data === typeof state[index][key])
        Vue.set(state[index], key, data)
    }
  },
  ADD_TRACK_TO_INDEX: function (
    state,
    { band_index, track_id }: { band_index: number; track_id: number }
  ) {
    if (band_index !== -1) {
      const track_set = state[band_index].track_set

      if (track_set && track_set.indexOf(track_id) === -1) {
        state.splice(band_index, 1, {
          ...state[band_index],
          track_set: [...track_set, track_id],
        })
      }
    }
  },
}

export const actions: ActionTree<BandSetState, RootState> = {
  async FETCH({ dispatch, commit }) {
    const bandSet: Band[] = await dispatch("GET")

    commit("RESET")
    commit("SET", bandSet)
    return bandSet
  },
  async GET() {
    try {
      const { results }: { results: Band[] } = await this.$axios.$get(
        "/band/band/self/"
      )
      return results || []
    } catch (err) {
      return []
    }
  },
  ADD_TRACK_TO_BAND: function (
    { commit, getters },
    { band_id, track_id }: { band_id: number; track_id: number }
  ) {
    commit("ADD_TRACK_TO_INDEX", {
      track_id,
      band_index: getters["GET_BAND_INDEX_FROM_ID"](band_id),
    })
  },
  MERGE_BAND_PATCH: function (
    { commit, getters },
    { band_patch, band_id }: { band_patch: Band; band_id: number | undefined }
  ) {
    const id = band_id || band_patch.id
    const index = getters.GET_BAND_INDEX_FROM_ID(id)

    if (id && index !== -1)
      commit("UPDATE_INDEX_WITH_PATCH", { band_patch, index })
  },
  FORCE_PICTURE_REFRESH: function (
    { commit, dispatch, state },
    noFollow: boolean
  ) {
    for (let i = state.length - 1; i >= 0; i--) {
      commit("UPDATE_INDEX_WITH_PATCH", {
        index: i,
        band_patch: { has_profile_picture: !state[i].has_profile_picture },
      })
    }
    if (!noFollow)
      Vue.nextTick(() => {
        dispatch("FORCE_PICTURE_REFRESH", true)
      })
  },
}

export const getters: GetterTree<BandSetState, RootState> = {
  GET_BAND_INDEX_FROM_ID(state) {
    return function (bandId: number) {
      return state.findIndex((band) => {
        return band.id === bandId
      })
    }
  },
  GET_BAND_INDEX_FROM_SLUG(state) {
    return function (bandSlug: string) {
      return state.findIndex((band) => {
        return band.slug === bandSlug
      })
    }
  },
  GET_BAND_PICTURE_FROM_SLUG: function () {
    return function (
      slug: string,
      {
        target,
        size,
      }: {
        size: PictureSize
        target: PictureTarget
      }
    ) {
      if (!size) size = "400_400"
      if (!target) target = "profile_picture"
      return picture_build_helper({
        kind: "band",
        slug,
        size,
        target,
      })
    }
  },
  GET_BAND_PICTURE_FROM_ID: function (state, getters) {
    return function (
      id: number,
      {
        target,
        size,
      }: {
        size: PictureSize
        target: PictureTarget
      }
    ) {
      const band = state[getters.GET_BAND_INDEX_FROM_ID(id)]

      //@ts-ignore
      if (band && band[`has_${target}`])
        return getters.GET_BAND_PICTURE_FROM_SLUG(band.slug, { target, size })
      else return `${process.env.IMAGE_BASE_URL}/band/${target}/${size}`
    }
  },
  LIVE_BANDS: function (state) {
    return state.filter((band) => !band.archived)
  },
  GET_ALL_BAND_PICTURES: function (state, getters) {
    return getters.LIVE_BANDS.map((band: Band) => {
      return getters.GET_BAND_PICTURE_FROM_ID(band.id, {
        target: "profile_picture",
        size: "200_200",
      })
    })
  },
  TRACK_COUNT(state) {
    return state.reduce((accumulator, band) => {
      return accumulator + band.track_set.length
    }, 0)
  },
}
